import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
} from 'react';
import { api } from '../api/client';
import { AiSpend } from '../ai/AiSpend';
import { acceptFiles, attachmentBlocks, attachmentLabel, type Attachment } from '../ai/attachments';
import { AttachButton, AttachChips } from '../ai/AttachmentControls';
import { useSpeechDictation } from '../lib/useSpeechDictation';
import { ProposalConfirm, type Proposal } from '../ai/ProposalConfirm';
import { Button, Card, Input, useToast } from '../ui';

/**
 * AI data entry — a chat that turns a Persian request (typed, or a photographed card / invoice the
 * model OCRs) into a data-entry action. The model PROPOSES (via tool use on the server); the user
 * confirms; the write then goes through the SAME validated + audited + RBAC route a human would use.
 * The AI never writes directly — human-in-the-loop by design.
 */

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const STARTERS = [
  'یک مشتری جدید به نام شرکت البرز با تلفن ۰۲۱۸۸۰۰۱۱۲۲ و سقف اعتبار ۵۰۰ میلیون اضافه کن',
  'تأمین‌کنندهٔ جدید: فولاد مبارکهٔ اصفهان',
  'نرخ دلار امروز را ۹۰ هزار تومان ثبت کن',
];

export function AiEntryChat() {
  const toast = useToast();
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<Proposal | null>(null);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [spendTick, setSpendTick] = useState(0);
  const [dragging, setDragging] = useState(false);
  const chatlogRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const didMount = useRef(false);
  // Dictation: speak the request instead of typing it. Final transcript is appended to the input.
  const dictation = useSpeechDictation((t) => setInput((cur) => (cur ? `${cur} ${t}` : t)));

  useEffect(() => {
    void api<{ configured: boolean }>('/api/ai/config').then((r) => {
      if (r.ok) setConfigured((r.body as { configured: boolean }).configured);
    });
  }, []);
  // Keep the newest message in view by scrolling the LOG's own overflow — never
  // the window. scrollIntoView() drags every scroll ancestor (incl. the page),
  // and skipping the first render matters: opening this screen/dock must not
  // yank the page down to the composer. This was the "auto-scroll on open" bug.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const el = chatlogRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, pending]);
  // Focus the composer on open, but WITHOUT scrolling it into view (native
  // autofocus/focus() would scroll the below-the-fold input up, moving the page).
  useEffect(() => {
    formRef.current?.querySelector('input')?.focus({ preventScroll: true });
  }, []);

  async function send(e: FormEvent) {
    e.preventDefault();
    const q = input.trim();
    if ((!q && files.length === 0) || busy) return;
    setError('');
    setPending(null);

    const attached = files;
    const displayText = [q, attachmentLabel(attached)].filter(Boolean).join('  ');
    // History as text; the current turn carries the real image/PDF blocks for OCR.
    const apiHistory = messages.map((m) => ({ role: m.role, content: m.content }));
    const apiUser = attached.length
      ? {
          role: 'user' as const,
          content: [
            ...attachmentBlocks(attached),
            { type: 'text', text: q || 'اطلاعات این تصویر را استخراج و ثبت کن.' },
          ],
        }
      : { role: 'user' as const, content: q };

    const nextUi: ChatMessage[] = [...messages, { role: 'user', content: displayText }];
    setMessages(nextUi);
    setInput('');
    setFiles([]);
    setBusy(true);
    const r = await api<{ reply?: string; proposal?: Proposal }>('/api/ai/entry', {
      method: 'POST',
      body: JSON.stringify({ messages: [...apiHistory, apiUser] }),
    });
    setBusy(false);
    if (!r.ok) {
      setError((r.body as { message?: string }).message ?? 'خطا در پاسخ');
      setMessages(messages);
      setInput(q);
      setFiles(attached);
      return;
    }
    setSpendTick((t) => t + 1);
    const body = r.body as { reply?: string; proposal?: Proposal };
    if (body.proposal) {
      setPending(body.proposal);
      setMessages([
        ...nextUi,
        { role: 'assistant', content: `پیشنهاد ثبت: ${body.proposal.summaryFa}` },
      ]);
    } else {
      setMessages([...nextUi, { role: 'assistant', content: body.reply ?? 'دستور مشخص نیست.' }]);
    }
  }

  // Drag-drop and clipboard-paste both funnel through the same validator the 📎
  // picker uses (type/size/count limits live in acceptFiles), so the three ways
  // to attach stay identical. Drop a photo, paste a screenshot, or click 📎.
  async function ingest(picked: File[]) {
    const list = picked.filter(Boolean);
    if (!list.length) return;
    const { accepted, error: err } = await acceptFiles(files, list);
    if (err) setError(err);
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    void ingest(Array.from(e.dataTransfer.files));
  }
  function onPaste(e: ClipboardEvent) {
    const imgs = Array.from(e.clipboardData.items)
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (imgs.length) void ingest(imgs);
  }

  return (
    <Card title="ثبت هوشمند با دستیار (Claude)">
      <p className="muted" style={{ marginTop: 0 }}>
        درخواست خود را به فارسی بنویسید (مشتری، تأمین‌کننده، یا نرخ دلار) — یا عکس کارت ویزیت، سربرگ
        یا فاکتور را پیوست کنید تا دستیار اطلاعات را استخراج کند: با دکمهٔ 📎، یا فایل را همین‌جا
        بکشید و رها کنید، یا تصویر را مستقیم بچسبانید (Ctrl+V). دستیار پیشنهاد می‌دهد؛ شما تأیید
        می‌کنید و سپس ثبت می‌شود — چیزی بدون تأیید شما ذخیره نمی‌شود.
      </p>

      <AiSpend refresh={spendTick} />

      {configured === false ? (
        <div className="banner">
          کلید API تنظیم نشده — ابتدا در «تنظیمات» بخش هوش مصنوعی، کلید Anthropic را وارد کنید.
        </div>
      ) : null}

      <div
        className={dragging ? 'chat-drop dragging' : 'chat-drop'}
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragging) setDragging(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={onDrop}
      >
        <div className="chatlog" ref={chatlogRef}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <p className="muted" style={{ marginTop: 0 }}>
                نمونه دستورها:
              </p>
              <div
                className="row"
                style={{ flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8 }}
              >
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="starter-chip"
                    onClick={() => setInput(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                className={m.role === 'user' ? 'bubble bubble-user' : 'bubble bubble-ai'}
              >
                {m.content}
              </div>
            ))
          )}
          {busy ? <div className="bubble bubble-ai muted">در حال پردازش…</div> : null}
        </div>
        {dragging ? <div className="chat-dropover">📎 فایل را اینجا رها کنید</div> : null}
      </div>

      {pending ? (
        <ProposalConfirm
          proposal={pending}
          onConfirmed={() => {
            setMessages((m) => [...m, { role: 'assistant', content: '✓ با موفقیت ثبت شد.' }]);
            setPending(null);
          }}
          onCancel={() => {
            setMessages((m) => [...m, { role: 'assistant', content: 'لغو شد — چیزی ثبت نشد.' }]);
            setPending(null);
          }}
        />
      ) : null}

      {error ? <div className="field-error">{error}</div> : null}
      <AttachChips files={files} setFiles={setFiles} />
      <form
        ref={formRef}
        onSubmit={send}
        onPaste={onPaste}
        className="row"
        style={{ marginTop: 10 }}
      >
        <div style={{ flex: 1 }}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="مثلاً: مشتری جدید «شرکت آریا» با تلفن ۰۲۱… — یا یک عکس پیوست کنید"
            disabled={busy || configured === false}
          />
        </div>
        <AttachButton
          files={files}
          setFiles={setFiles}
          onError={setError}
          disabled={busy || configured === false}
        />
        {/* Voice input is always visible so it's discoverable; if the browser lacks speech
            recognition (e.g. an old Safari) we say so on tap instead of silently hiding it. */}
        <button
          type="button"
          className={dictation.listening ? 'msg-icon danger' : 'msg-icon'}
          aria-label={dictation.listening ? 'توقف گفتار' : 'گفتن دستور با میکروفون'}
          title={dictation.supported ? 'ورودی صوتی' : 'ورودی صوتی (نیازمند کروم/سافاری جدید)'}
          aria-pressed={dictation.listening}
          onClick={() => {
            if (!dictation.supported) {
              toast(
                'ورودی صوتی در این مرورگر پشتیبانی نمی‌شود — از Chrome یا Safari جدید استفاده کنید',
                'error',
              );
              return;
            }
            if (dictation.listening) dictation.stop();
            else dictation.start();
          }}
          disabled={busy || configured === false}
        >
          {dictation.listening ? '■' : '🎤'}
        </button>
        <Button type="submit" loading={busy} disabled={configured === false}>
          ارسال
        </Button>
      </form>
    </Card>
  );
}
